import React from 'react';

import { CircadianEvent, LightRecipeDoc, RecipeSection } from '@ledium/types';
import cloneDeep from 'clone-deep';

import lediumApi from '../utils/lediumApi';

interface ChartPoint {
    time: number;
    value: number;
}

const useSectionData = (section: RecipeSection) => {
    const [sectionData, setSectionData] = React.useState<RecipeSection>({
        ...section,
        circadianEvents: section.circadianEvents ? cloneDeep(section.circadianEvents) : []
    });
    const [spectrumData, setSpectrumData] = React.useState<Record<string, number>>({});
    const [chartData, setChartData] = React.useState<ChartPoint[]>([]);

    const loadSection = (sectionToLoad: RecipeSection) => {
        setSectionData({
            ...sectionToLoad,
            circadianEvents: sectionToLoad.circadianEvents ? cloneDeep(sectionToLoad.circadianEvents) : []
        });
    };

    React.useEffect(() => {
        if (section.id) {
            setChartData([]);
            setSectionData({
                ...section,
                circadianEvents: section.circadianEvents ? cloneDeep(section.circadianEvents) : []
            });
        }
    }, [section]);

    React.useEffect(() => {
        if (sectionData.circadianEvents.length) {
            const lightRecipeCache: Record<string, LightRecipeDoc> = {};

            if (!Object.keys(spectrumData).length) {
                (async () => {
                    let newSpectrumData: Record<string, number> = {};
                    for (const i in sectionData.circadianEvents) {
                        const event = sectionData.circadianEvents[i];
                        if (event.lightRecipeId && spectrumData[event.lightRecipeId] === undefined) {
                            if (!lightRecipeCache[event.lightRecipeId]) {
                                const fetched = await lediumApi.get<LightRecipeDoc>(`lightRecipes/${event.lightRecipeId}`);
                                if (fetched) {
                                    lightRecipeCache[event.lightRecipeId] = fetched;
                                }
                            }
                            if (lightRecipeCache[event.lightRecipeId]?.spectrogram) {
                                const lightRecipeSumLight = Number.isInteger(event.sumLightsIntensity)
                                    ? (event.sumLightsIntensity as number)
                                    : lightRecipeCache[event.lightRecipeId].spectrogramRanges.pfd.sum;
                                newSpectrumData = {
                                    ...newSpectrumData,
                                    [event.lightRecipeId]: lightRecipeSumLight
                                };
                            }
                        }
                    }
                    if (Object.keys(newSpectrumData).length) {
                        setSpectrumData({ ...spectrumData, ...newSpectrumData });
                    }
                })();
            }
            const chart: ChartPoint[] = [];
            [...sectionData.circadianEvents]
                .sort((a, b) => (+a.hour + a.minute / 60 > +b.hour + b.minute / 60 ? 1 : -1))
                .forEach((event: CircadianEvent) => {
                    const spectrumSumLight = spectrumData[event.lightRecipeId] ?? 0;
                    chart.push({
                        time: event.hour + event.minute / 60,
                        value: spectrumSumLight
                    });
                });
            setChartData(chart);
        }
    }, [sectionData.circadianEvents, spectrumData]);

    return { sectionData, setSectionData, chartData, loadSection };
};

export default useSectionData;
